import { Parameter } from '../script/parameter.js'
import { combineLatest, Observable } from 'rxjs'
import { ExecutionContext } from '../script/execution-context.js'
import { DeclarationsContainer, ModuleIdType } from '../types.js'
import { ExecutionContextName } from '../script/execution-context.js'
import { map, tap, shareReplay } from 'rxjs/operators'
import { createCodeBlockWriter } from './data-util.js'
export type RawTypeDeclarationsType = Partial<
	Record<
		ExecutionContextName,
		Record<string, Record<string, DeclarationsContainer>>
	>
>

export class DeclarationUtil {
	static calculateTypeDeclarations(
		rawTypeDeclarations: RawTypeDeclarationsType,
	) {
		const modules: string[] = []

		const writer = createCodeBlockWriter()
		for (const executionContextName in rawTypeDeclarations) {
			// console.log(executionContextName)
			const moduleDeclarations =
				rawTypeDeclarations[executionContextName as ExecutionContextName]
			if (!moduleDeclarations) continue

			const moduleIds = Object.keys(moduleDeclarations)
			const executionContextModuleName = `@${executionContextName}`
			const moduleModuleNames: string[] = []

			for (const moduleId of moduleIds) {
				const boDeclarationsMap = moduleDeclarations[moduleId]
				const declarationEntries = Object.entries(boDeclarationsMap)

				const moduleModuleName = `${executionContextModuleName}/${moduleId}`
				moduleModuleNames.push(moduleModuleName)
				const boModuleNames: string[] = []

				for (const [boId, declContainer] of declarationEntries) {
					const boModuleName = `${moduleModuleName}/${boId}`
					boModuleNames.push(boModuleName)

					writer.write(`declare module '${boModuleName}'`).block(() => {
						for (const [importName, importPath] of Object.entries(
							declContainer.imports,
						)) {
							if (importName?.startsWith('*')) {
								writer.writeLine(`import ${importName} from '${importPath}'`)
							} else {
								writer.writeLine(
									`import { ${importName} } from '${importPath}'`,
								)
							}
						}
						const relevantModuleIds = new Set([
							moduleId,
							// TODO: dynamically determine imports
							'System',
							'Common',
						])
						for (const relevantModuleId of relevantModuleIds) {
							writer.writeLine(
								`import * as ${relevantModuleId} from '${executionContextModuleName}/${relevantModuleId}'`,
							)
						}
						writer.writeLine(`import This = ${moduleId}`)
						for (const declaration of declContainer.declarations) {
							writer.writeLine(declaration)
						}

						// writer.writeLine(`import This = ${moduleName}`)
						// writer.writeLine(`export * from '${moduleName}'`)
					})
				}

				writer.write(`declare module '${moduleModuleName}'`).block(() => {
					for (const boModuleName of boModuleNames) {
						writer.writeLine(`export * from '${boModuleName}'`)
					}
				})
			}

			writer
				.write(`declare module '${executionContextModuleName}'`)
				.block(() => {
					for (const moduleId of moduleIds) {
						writer.writeLine(
							`export * as ${moduleId} from '@${executionContextName}/${moduleId}'`,
						)
					}
				})

			// writer.write(`declare module '${executionContextName}'`).block(() => {
			// 	// First loop: write imports for all modules
			// 	const imports: Record<string, string> = {}
			// 	for (const moduleId in moduleDeclarations) {
			// 		const boDeclarationsMap = moduleDeclarations[moduleId]
			// 		for (const declContainer of Object.values(boDeclarationsMap)) {
			// 			Object.assign(imports, declContainer.imports)
			// 		}
			// 	}
			// 	for (const importObj of Object.entries(imports)) {
			// 		if (importObj[0]?.startsWith('*')) {
			// 			writer.writeLine(`import ${importObj[0]} from '${importObj[1]}'`)
			// 		} else {
			// 			writer.writeLine(
			// 				`import { ${importObj[0]} } from '${importObj[1]}'`,
			// 			)
			// 		}
			// 	}

			// 	// Second loop: write module declarations
			// 	for (const moduleId in moduleDeclarations) {
			// 		const boDeclarationsMap = moduleDeclarations[moduleId]
			// 		const writeDeclarations = (isForNamespace: boolean) => {
			// 			;[...Object.values(boDeclarationsMap)].forEach(
			// 				(declarationsContainer) => {
			// 					if (
			// 						declarationsContainer.doEncloseInNamespace == isForNamespace
			// 					) {
			// 						declarationsContainer.declarations.forEach((declaration) => {
			// 							declaration = declaration.replace(
			// 								/\bThis\./g,
			// 								`${moduleId}.`,
			// 							)
			// 							writer.writeLine(declaration)
			// 						})
			// 					}
			// 				},
			// 			)
			// 		}

			// 		writer.write(`export namespace ${moduleId}`).block(() => {
			// 			writer.writeLine(`import This = ${moduleId}`)
			// 			writeDeclarations(true)
			// 		})
			// 		writeDeclarations(false)
			// 	}
			// })
		}

		return writer.toString()
	}

	static getWrapperImports$(
		executionContext: ExecutionContext,
		rawTypeDeclarations$: Observable<RawTypeDeclarationsType>,
		thisModuleId$: Observable<ModuleIdType>,
		importModuleIds$: Observable<string[] | '*'>,
	) {
		return combineLatest([
			rawTypeDeclarations$,
			thisModuleId$,
			importModuleIds$,
		]).pipe(
			map(([rawTypeDeclarations, thisModuleId, importModuleIds]) => {
				if (!executionContext) return ''

				const importItems = new Set<string>()
				if (importModuleIds == '*') {
					Object.keys(
						rawTypeDeclarations?.[executionContext.contextName] ?? {},
					).forEach((moduleId) => {
						importItems.add(moduleId)
					})
				} else {
					importModuleIds.forEach((moduleId) => {
						importItems.add(moduleId)
					})
				}

				if (thisModuleId != '*') {
					importItems.add(thisModuleId)
					importItems.add(`${thisModuleId} as This`)
				}

				const imports = [
					`import { ${[...importItems].join(', ')} } from '@${executionContext.contextNameWithSuffixes}'`,
					`import { IdType, DeepReadonly } from '@lg/shared'`,
				]

				// TODO: remove concept of system modules
				const sysModuleName = executionContext.getSysModuleName()
				if (sysModuleName)
					imports.push(`import * as Sys from '${sysModuleName}'`)

				if (executionContext.needsAppStateDeclaration()) {
					imports.push('declare const app: This.$AppState')
				}
				// TODO: move screen/app state to This.$Screen[boId].State and remove from here. Update type in Screen.createVirtualRootBlockAndLinkParents from any to This.$Screen[boId].State
				if (executionContext.needsScreenStateDeclaration()) {
					imports.push(
						`import { $ScreenState } from '@${executionContext.contextName}/${thisModuleId}/__ScreenState'`,
					)
					imports.push('declare const screen: $ScreenState')
				}
				return imports.join('; ')
			}),
			shareReplay(1),
		)
	}

	static async declarationFromData(data: any): Promise<string> {
		if (data == null) return 'any'
		if (data instanceof Array)
			return `${await this.declarationFromData(data[0])}[]`
		if (data instanceof Promise)
			return `Promise<${await this.declarationFromData(await data)}>`
		if (data instanceof Date) return 'Date'

		if (typeof data == 'object') {
			const props = await Promise.all(
				Object.keys(data).map(
					async (key) =>
						`"${key}": ${await this.declarationFromData(data[key])}`,
				),
			)
			return `{ ${props.join(', ')} }`
		}

		return typeof data
	}
}
